import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faDollarSign, faBolt, faLock, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { ProfileCard, ProfileDetailsDrawer } from '../index';
import ValueCard from './ValueCard';
import { useSearch } from '../hooks/useSearch';
import { useShortlistContext } from '../../../contexts/ShortlistContext';
import { useFxRates } from '../../../shared/hooks/useFxRates';

const SearchPage: React.FC = () => {
  const location = useLocation();
  const { shortUrl } = useParams();
  const isCampaign = Boolean(shortUrl);
  const [campaignTitle, setCampaignTitle] = useState<string | null>(null);
  const [campaignCurrency, setCampaignCurrency] = useState<string>('USD');
  const [localProfiles, setLocalProfiles] = useState<any[]>([]);
  const {
    profiles,
    loading,
    hasSearched,
    fetchProfiles,
  } = useSearch();

  const {
    addProfileToShortlist,
    removeFromShortlist,
    isInShortlist,
  } = useShortlistContext();

  // FX rates for currency conversion
  const { convert, roundToNearestFive } = useFxRates();

  // Helper function to convert rates
  const convertRate = async (rate: number, fromCurrency: string, toCurrency: string): Promise<number> => {
    console.log(`Converting ${rate} ${fromCurrency} to ${toCurrency}`);
    console.log(`Current campaignCurrency state: ${campaignCurrency}`);
    if (fromCurrency === toCurrency) {
      const rounded = roundToNearestFive(rate);
      console.log(`Same currency, rounded to: ${rounded}`);
      return rounded;
    }
    try {
      const converted = await convert(rate, fromCurrency as any, toCurrency as any);
      const rounded = roundToNearestFive(converted);
      console.log(`Converted ${rate} ${fromCurrency} to ${converted} ${toCurrency}, rounded to: ${rounded}`);
      return rounded;
    } catch (error) {
      console.error('Currency conversion failed:', error);
      const rounded = roundToNearestFive(rate);
      console.log(`Fallback: rounded original rate to: ${rounded}`);
      return rounded;
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const profilesPerPage = 30;
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showProtectionBar, setShowProtectionBar] = useState(true);

  // Handle incoming search state from navigation
  useEffect(() => {
    const searchState = location.state?.searchState;
    if (searchState) {
      // Restore search state (e.g., current page, filters, etc.)
      if (searchState.currentPage) {
        setCurrentPage(searchState.currentPage);
      }
      // Add other state restoration logic here as needed
    }
  }, [location.state]);


  // Create search state object to pass to profile details (for future use)
  // const searchState = useMemo(() => ({
  //   currentPage,
  //   totalPages: Math.ceil(profiles.length / profilesPerPage),
  //   profilesCount: profiles.length,
  //   // Add other search state properties as needed
  // }), [currentPage, profiles.length, profilesPerPage]);

  // Pagination logic
  const list = isCampaign ? localProfiles : profiles;
  const totalPages = Math.ceil(list.length / profilesPerPage);
  const startIndex = (currentPage - 1) * profilesPerPage;
  const endIndex = startIndex + profilesPerPage;
  const currentProfiles = list.slice(startIndex, endIndex);

  // Auto-load profiles depending on route
  useEffect(() => {
    const load = async () => {
      if (isCampaign && shortUrl) {
        try {
          const res = await fetch(`/api/campaigns/${shortUrl}/view`);
          if (!res.ok) throw new Error('Failed to load campaign');
          const data = await res.json();
          setCampaignTitle(data?.title || null);
          const campaignCurrencyFromAPI = data?.currency || 'USD';
          setCampaignCurrency(campaignCurrencyFromAPI);
          console.log('Campaign currency set to:', campaignCurrencyFromAPI);

          // Transform campaign profiles to match Profile interface
          const campaignProfiles = Array.isArray(data?.profiles) ? data.profiles : [];
          const transformedProfiles = await Promise.all(campaignProfiles.map(async (profile: any, index: number) => {
            try {
              // Calculate total experience from all experiences
              const totalExperienceMonths = profile.experiences?.reduce((total: number, exp: any) =>
                total + (exp.totalDurationMonths || 0), 0) || 0;

              // Get the most recent role from the first experience
              const currentRole = profile.experiences?.[0]?.roles?.[0];

              // Get all unique skills from all experiences and roles
              const allSkills = new Set<string>();
              profile.experiences?.forEach((exp: any) => {
                exp.roles?.forEach((role: any) => {
                  role.skills?.forEach((skill: string) => {
                    if (skill && typeof skill === 'string') {
                      allSkills.add(skill);
                    }
                  });
                });
              });

              // Fallback skills if none found
              const skills = Array.from(allSkills).slice(0, 6);
              if (skills.length === 0) {
                skills.push('JavaScript', 'React', 'Node.js');
              }

              return {
                id: profile.id || `profile-${index}`,
                title: profile.title || currentRole?.title || 'Software Engineer',
                experience: `${Math.round(totalExperienceMonths / 12)} years of experience`,
                verification: 'Verified by DreamDev',
                skills: skills,
                industry: (profile.industry as string) || (Array.isArray(profile.industries) ? profile.industries[0] : '') || 'Technology',
                industries: profile.industries,
                candidateName: profile.candidateName,
                profileUrl: profile.profileUrl,
                summary: profile.summary,
                email: profile.email,
                phone: profile.phone,
                linkedInLink: profile.linkedInLink,
                experiences: profile.experiences || [],
                anonymizedProfile: profile.anonymizedProfile,
                dayRate: profile.dayRate ? await convertRate(profile.dayRate, profile.dayRateCurrency || 'USD', campaignCurrencyFromAPI) : undefined,
                dayRateCurrency: campaignCurrencyFromAPI,
              };
            } catch (error) {
              console.error('Error transforming campaign profile:', profile, error);
              // Return a fallback profile
              return {
                id: profile.id || `profile-${index}`,
                title: profile.title || 'Software Engineer',
                experience: '0 years of experience',
                verification: 'Verified by DreamDev',
                skills: ['JavaScript', 'React', 'Node.js'],
                industry: (profile.industry as string) || (Array.isArray(profile.industries) ? profile.industries[0] : '') || 'Technology',
                candidateName: profile.candidateName,
                profileUrl: profile.profileUrl,
                summary: profile.summary,
                email: profile.email,
                phone: profile.phone,
                linkedInLink: profile.linkedInLink,
                experiences: profile.experiences || [],
                anonymizedProfile: profile.anonymizedProfile,
                dayRate: profile.dayRate ? await convertRate(profile.dayRate, profile.dayRateCurrency || 'USD', campaignCurrencyFromAPI) : undefined,
                dayRateCurrency: campaignCurrencyFromAPI,
              };
            }
          }));

          setLocalProfiles(transformedProfiles);
        } catch (e) {
          console.error(e);
          setCampaignTitle(null);
          setLocalProfiles([]);
        }
      } else {
        fetchProfiles({
          selectedRoles: [],
          selectedSkills: [],
          industryFilter: '',
          rolesSearch: '',
          skillsSearch: '',
        });
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCampaign, shortUrl]);

  // Reset to first page when profiles change
  useEffect(() => {
    setCurrentPage(1);
  }, [isCampaign ? localProfiles.length : profiles.length]);

  const handleBookmark = (profileId: string) => {
    if (isInShortlist(profileId)) {
      removeFromShortlist(profileId);
    } else {
      // Find the profile data from the appropriate source
      const profileSource = isCampaign ? localProfiles : profiles;
      const profile = profileSource.find(p => p.id === profileId);
      if (profile) {
        addProfileToShortlist({
          id: profile.id,
          title: profile.title,
          experience: profile.experience,
          skills: profile.skills,
          industries: profile.industries,
        });
      }
    }
  };

  const handleViewProfile = (profileData: any) => {
    setSelectedProfile(profileData);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedProfile(null);
  };

  const scrollToFirstProfile = () => {
    // Find the first profile card and scroll to it
    const firstProfileCard = document.querySelector('[data-profile-card]');
    if (firstProfileCard) {
      firstProfileCard.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };


  const renderHeader = () => (
    <div
      className="w-full mb-8"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'var(--dreamdev-bg-gradient-hero)'
      }}
    >
      <div className="mx-auto px-4 pt-8" style={{ maxWidth: '1200px' }}>
        {/* Orange accent line */}
        <div
          className="mb-2 mt-6"
          style={{
            width: '30px',
            height: '2px',
            backgroundColor: 'var(--dreamdev-accent-500)',
            borderRadius: '2px'
          }}
        />

        {/* NEARSHORE STAFF AUGMENTATION label */}
        <div
          className="mb-2"
          style={{
            fontSize: '16px',
            color: 'var(--dreamdev-primary-400)',
            fontWeight: '600',
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.4'
          }}
        >
          NEARSHORE STAFF AUGMENTATION
        </div>

        {/* Main headline */}
        <h1
          className="mb-2"
          style={{
            fontSize: '64px',
            fontWeight: '700',
            color: 'var(--dreamdev-text-cream)',
            fontFamily: 'Source Sans 3, sans-serif',
            lineHeight: '1.2'
          }}
        >
          {isCampaign && campaignTitle ? (
            <>{typeof campaignTitle === "string"
              ? (
                <>
                  {campaignTitle.split(/(#.*?#)/g).map((part, idx) =>
                    part.match(/^#(.*)#$/)
                      ? <span key={idx} style={{ color: 'var(--dreamdev-accent-500)' }}>{part.slice(1, -1)}</span>
                      : <React.Fragment key={idx}>{part}</React.Fragment>
                  )}
                </>
              )
              : null
            }
            </>
          ) : (
            <>Senior <span style={{ color: 'var(--dreamdev-accent-500)' }}>Engineers</span> Available to Interview This Week</>
          )}
        </h1>

        {/* First descriptive line - Call to Action */}
        <p
          className="mb-6"
          style={{
            fontSize: '20px',
            color: '#E9F7FF',
            fontWeight: '600',
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.4'
          }}
        >
          Stop waiting months for senior hires. Our vetted engineers are available now, not in 3 months.
        </p>

        {/* Value Cards */}
        <div className="flex flex-col sm:flex-row gap-10 justify-left items-center sm:items-left mb-16">
          <ValueCard
            icon={faBolt}
            title="Fast Access"
            description="First interview within 48 hours. Start dates in 2-4 weeks, not months."
          />
          <ValueCard
            icon={faCheckCircle}
            title="Pre-Vetted Quality"
            description="Every engineer interviewed by our senior technical team."
          />
          <ValueCard
            icon={faDollarSign}
            title="Cost Effective"
            description="No recruitment fees, no hiring overhead, no long-term commitment."
          />
        </div>
      </div>

      {/* Trusted By Bar */}
      <div
        className="w-full flex items-center justify-center"
        style={{
          height: '92px',
          backgroundColor: 'rgba(27, 30, 40, 0.5)',
        }}
      >
        {/* Desktop Layout */}
        <div className="hidden sm:flex justify-between items-center space-x-8" style={{ width: '100%', maxWidth: '1200px' }}>
          <span
            style={{
              color: '#CEECFF',
              fontSize: '20px',
              fontWeight: '600',
              fontFamily: 'Inter, sans-serif'
            }}
          >
            Trusted by
          </span>

          <img src="/logos/nbi.svg" alt="NBI" style={{ height: '60px' }} />
          <img src="/logos/lumon.svg" alt="Lumon" style={{ height: '20px' }} />
          <img src="/logos/secto.svg" alt="Secto" style={{ height: '20px' }} />
          <img src="/logos/game-golf.svg" alt="Game Golf" style={{ height: '20px' }} />
        </div>

        {/* Mobile Carousel */}
        <div className="sm:hidden w-full overflow-hidden">
          <div
            className="flex items-center space-x-8 animate-scroll"
            style={{
              animation: 'scroll 20s linear infinite',
              width: 'max-content'
            }}
          >
            {/* Duplicate logos for seamless loop */}
            <img src="/logos/nbi.svg" alt="NBI" style={{ height: '40px' }} />
            <img src="/logos/lumon.svg" alt="Lumon" style={{ height: '20px' }} />
            <img src="/logos/secto.svg" alt="Secto" style={{ height: '20px' }} />
            <img src="/logos/game-golf.svg" alt="Game Golf" style={{ height: '20px' }} />
            <img src="/logos/nbi.svg" alt="NBI" style={{ height: '40px' }} />
            <img src="/logos/lumon.svg" alt="Lumon" style={{ height: '20px' }} />
            <img src="/logos/secto.svg" alt="Secto" style={{ height: '20px' }} />
            <img src="/logos/game-golf.svg" alt="Game Golf" style={{ height: '20px' }} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoadingSpinner = () => (
    <div className="flex items-center justify-center py-8">
      <div className="flex flex-col items-center space-y-4">
        {/* Spinner */}
        <div className="relative">
          <div className="w-12 h-12 border-4 border-gray-200 rounded-full animate-spin"></div>
          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-orange-500 rounded-full animate-spin"></div>
        </div>
        <div className="text-lg font-medium" style={{ color: '#FF5C3E' }}>Loading profiles...</div>
      </div>
    </div>
  );

  const renderPagination = (withScroll = false) => (
    totalPages > 1 && (
      <div className="flex justify-end mb-6 mt-8 px-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              setCurrentPage(Math.max(1, currentPage - 1));
              if (withScroll) {
                setTimeout(scrollToFirstProfile, 100);
              }
            }}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-bold uppercase transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            style={{
              height: '38px',
              backgroundColor: currentPage === 1 ? 'var(--dreamdev-bg-primary)' : 'var(--dreamdev-primary)',
              color: currentPage === 1 ? 'var(--dreamdev-text-primary)' : 'var(--dreamdev-text-white)',
              borderRadius: 'var(--dreamdev-radius-md)',
              border: `1px solid ${currentPage === 1 ? 'var(--dreamdev-border)' : 'var(--dreamdev-primary)'}`
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
          </button>

          <div className="flex flex-wrap justify-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => {
                  setCurrentPage(page);
                  if (withScroll) {
                    setTimeout(scrollToFirstProfile, 100);
                  }
                }}
                className="px-3 py-2 text-sm font-bold uppercase transition-colors duration-200 flex items-center justify-center"
                style={{
                  height: '38px',
                  backgroundColor: currentPage === page ? 'var(--dreamdev-primary)' : 'var(--dreamdev-bg-primary)',
                  color: currentPage === page ? 'var(--dreamdev-text-white)' : 'var(--dreamdev-text-primary)',
                  borderRadius: 'var(--dreamdev-radius-md)',
                  border: `1px solid ${currentPage === page ? 'var(--dreamdev-primary)' : 'var(--dreamdev-border)'}`
                }}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => {
              setCurrentPage(Math.min(totalPages, currentPage + 1));
              if (withScroll) {
                setTimeout(scrollToFirstProfile, 100);
              }
            }}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-bold uppercase transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            style={{
              height: '38px',
              backgroundColor: currentPage === totalPages ? 'var(--dreamdev-bg-primary)' : 'var(--dreamdev-primary)',
              color: currentPage === totalPages ? 'var(--dreamdev-text-primary)' : 'var(--dreamdev-text-white)',
              borderRadius: 'var(--dreamdev-radius-md)',
              border: `1px solid ${currentPage === totalPages ? 'var(--dreamdev-border)' : 'var(--dreamdev-primary)'}`
            }}
          >
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
          </button>
        </div>
      </div>
    )
  );

  const renderMainContent = () => {
    if (loading) {
      return (
        <div className="w-full flex flex-col items-center" style={{ backgroundColor: 'var(--dreamdev-bg-primary)' }}>
          {renderHeader()}
          {renderLoadingSpinner()}
        </div>
      );
    }

    if (hasSearched && profiles.length === 0) {
      return (
        <div className="w-full flex flex-col items-center" style={{ backgroundColor: 'var(--dreamdev-bg-primary)' }}>
          {renderHeader()}
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="text-xl mb-2" style={{ color: 'var(--dreamdev-text-primary)' }}>No profiles found</div>
              <div className="text-sm" style={{ color: 'var(--dreamdev-text-secondary)' }}>Try adjusting your search criteria</div>
            </div>
          </div>
        </div>
      );
    }


    return (
      <div className="w-full flex flex-col items-center mb-16" style={{ backgroundColor: 'var(--dreamdev-bg-primary)' }}>
        {renderHeader()}

        {/* Profile Protection Bar */}
        {showProtectionBar && (
          <div
            className="flex-1 flex flex-col sm:flex-row p-6 sm:items-start items-start gap-4 mx-4"
            style={{
              backgroundColor: '#FFF8E1',
              border: '1px solid #FFB74D',
              borderRadius: '8px',
              maxWidth: '1200px'
            }}
          >
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon
                icon={faLock}
                style={{
                  color: '#DC6803',
                  fontSize: '16px'
                }}
              />
              <span
                style={{
                  color: '#DC6803',
                  fontSize: '16px',
                  fontWeight: '700',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                Profile Protection
              </span>
            </div>

            <div className="flex-1">
              <p
                style={{
                  color: '#DC6803',
                  fontSize: '16px',
                  fontWeight: '400',
                  fontFamily: 'Source Sans 3, sans-serif',
                  margin: '0',
                  lineHeight: '1.4'
                }}
              >
                We show verified skills and experience while protecting our engineers' identities.
              </p>
              <p
                style={{
                  color: '#DC6803',
                  fontSize: '16px',
                  fontWeight: '400',
                  fontFamily: 'Source Sans 3, sans-serif',
                  margin: '0',
                  lineHeight: '1.4'
                }}
              >
                Full profiles, portfolios, and direct interviews available after initial discussion.
              </p>
            </div>

            <button
              onClick={() => setShowProtectionBar(false)}
              className="flex items-center justify-center self-end sm:self-start"
              style={{
                color: '#DC6803',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '14px' }} />
            </button>
          </div>
        )}

        <div className="flex-1 text-left w-full my-8 px-4" style={{ maxWidth: '1200px' }}>
          <h2
            className="text-2xl font-bold"
            style={{
              color: '#03238C',
              fontWeight: '700',
              fontFamily: 'Inter, sans-serif',
              fontSize: '32px'
            }}
          >
            Browse Profiles and Build Your Shortlist
          </h2>
          <p className="mt-1" style={{ color: '#6B7280' }}>
            Browse our vetted talent pool, select and save the profiles you're most interested in to create a custom shortlist that matches your project's requirements.
          </p>
        </div>

        {/* Pagination Controls - Above Results, Right Aligned */}
        {renderPagination(false)}

        {/* Profile Cards - Full Width, One Per Row */}
        <div className="space-y-4 flex flex-col items-center px-4" style={{ maxWidth: '1200px' }}>
          {currentProfiles.map((profile, index) => (
            <div key={profile.id} data-profile-card={index === 0 ? 'first' : undefined}>
              <ProfileCard
                id={profile.id}
                title={profile.title}
                experience={profile.experience}
                verification={profile.verification}
                skills={profile.skills}
                industry={profile.industry}
                industries={profile.industries}
                summary={profile.summary}
                onBookmark={() => handleBookmark(profile.id)}
                isBookmarked={isInShortlist(profile.id)}
                profileData={profile}
                onViewProfile={handleViewProfile}
                showDayRate={isCampaign}
                dayRate={profile.dayRate}
                dayRateCurrency={profile.dayRateCurrency}
              />
            </div>
          ))}
        </div>

        {/* Pagination Controls - Below Results, Right Aligned */}
        {renderPagination(true)}
      </div>
    );
  };

  return (
    <>
      {renderMainContent()}


      {/* Profile Details Drawer */}
      <ProfileDetailsDrawer
        profile={selectedProfile}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        onAddToShortlist={handleBookmark}
        isInShortlist={selectedProfile ? isInShortlist(selectedProfile.id) : false}
      />

    </>
  );
};

export default SearchPage;